/**
 * Line icons of the support window (24px grid, stroke = currentColor), plus the
 * Tasks page's row button glyph, which is the same speech bubble.
 */
import React from 'react';

const BUBBLE_PATH = 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z';

const Svg = ({ size = 18, weight = 2, style, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={weight}
    strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true">{children}</svg>
);

export const Bubble = ({ size = 24 }) => <Svg size={size}><path d={BUBBLE_PATH} /></Svg>;
export const ArrowUp = () => <Svg size={16} weight={2.6}><path d="M12 19V5M5 12l7-7 7 7" /></Svg>;
export const Check = () => <Svg size={16} weight={2.8}><path d="M5 12.5l4.5 4.5L19 7.5" /></Svg>;
export const Collapse = () => <Svg size={20} weight={2.2}><path d="M6 9l6 6 6-6" /></Svg>;
export const More = () => <Svg size={20} weight={2.6}><path d="M5 12h.01M12 12h.01M19 12h.01" /></Svg>;
export const Pencil = () => (
  <Svg size={15} weight={2.3}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></Svg>
);
export const Plus = () => <Svg size={20} weight={2.2}><path d="M12 5v14M5 12h14" /></Svg>;
export const Mic = () => (
  <Svg size={19}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" /></Svg>
);
export const Close = ({ size = 16 }) => <Svg size={size} weight={2.6}><path d="M6 6l12 12M18 6L6 18" /></Svg>;
export const CheckCircle = () => (
  <Svg size={14} weight={2.2}><circle cx="12" cy="12" r="9.5" /><path d="M8 12.4l2.8 2.8L16.2 9.6" /></Svg>
);

/**
 * A screenshot, not a photo: a plain camera reads as "upload a picture", while
 * the button means "capture THIS screen". The dashed selection frame is the
 * learned sign for that; the camera sits inside, a little low so the frame stays
 * visible above it. On the right and at the bottom the dashes stop early, where
 * two lines that almost touch would melt into a blob at this size.
 */
export const ScreenCapture = () => (
  <Svg size={23} weight={1.7}>
    <path d="M2.4 7V5.2a2.8 2.8 0 0 1 2.8-2.8H7" />
    <path d="M10.2 2.4h3.6" />
    <path d="M17 2.4h1.8a2.8 2.8 0 0 1 2.8 2.8V7" />
    <path d="M2.4 10.8v2.8" />
    <path d="M21.6 10.8v1.2" />
    <path d="M2.4 17.2v1.6a2.8 2.8 0 0 0 2.8 2.8h1.2" />
    <path d="M7.2 13a1.6 1.6 0 0 1 1.6-1.6h1.4l.9-1.5h3.8l.9 1.5h1.4A1.6 1.6 0 0 1 18.8 13v5a1.6 1.6 0 0 1-1.6 1.6H8.8A1.6 1.6 0 0 1 7.2 18z" />
    <circle cx="13" cy="15.6" r="2.3" />
  </Svg>
);

/* Image editor tools. Crosshair with selection corners, the learned sign for cutting out a region: the
   corners say "drag a rectangle", the cross says "start exactly here". */
export const Crosshair = () => (
  <Svg size={16} weight={2}>
    <path d="M3 8V4.8A1.8 1.8 0 0 1 4.8 3H8" /><path d="M16 3h3.2A1.8 1.8 0 0 1 21 4.8V8" />
    <path d="M21 16v3.2a1.8 1.8 0 0 1-1.8 1.8H16" /><path d="M8 21H4.8A1.8 1.8 0 0 1 3 19.2V16" />
    <path d="M12 8.5v7M8.5 12h7" />
  </Svg>
);
/** Diagonal arrow (ArrowUp points up and belongs to the send button). */
export const ArrowDiagonal = () => <Svg size={16} weight={2.3}><path d="M19 5L6 18" /><path d="M19 12.5V5h-7.5" /></Svg>;
export const Undo = () => <Svg size={15} weight={2.2}><path d="M4 9h11a5 5 0 0 1 0 10h-6" /><path d="M8 5L4 9l4 4" /></Svg>;

/** Messenger ticks: one = delivered, two = read. */
export const ReadTicks = ({ read, title }) => (
  <span className={`sw-haken${read ? ' gelesen' : ''}`} title={title} aria-label={read ? 'gelesen' : 'zugestellt'}>
    <svg width="16" height="11" viewBox="0 0 16 11" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {read
        ? <><path d="M1 6l3 3 6-7.5" /><path d="M6.6 8.6l.9.9 6-7.5" /></>
        : <path d="M3.5 6l3 3 6-7.5" />}
    </svg>
  </span>
);

/**
 * Tasks page, "write to the DeskBot team about this call": exactly the
 * starter's bubble in the starter's blue, so it is one sign to learn, not two.
 * Colour via `style` because SVG presentation attributes cannot take var();
 * the inline style also wins over the wrapper's stroke="currentColor".
 */
const TASK_ICON_STYLE = { stroke: 'var(--color-secondary, #2F6BD7)' };
export const TaskSupportIcon = () => <Svg style={TASK_ICON_STYLE}><path d={BUBBLE_PATH} /></Svg>;
